import { useApiMutation } from './useApiMutation';
import { useApiQuery } from './useApiQuery';
import { bankIncomingQueryKeys } from './useBankIncoming';
import { queryKeys } from './queryKeys';
import { ledgerQueryKeys } from './useGeneralLedger';
import { currencyConversionService } from '@/services/currencyConversionService';
import type {
  ConversionFromBankLinesCreate,
  CurrencyConversionCreateResponse,
  ConversionProposal,
  FundingChain,
  CurrencyConversionCreate,
  CurrencyConversionRead,
} from '@/types/currency-conversion';
import type { ForeignCashPositionsResponse } from '@/types/foreign-cash-positions';
import type { FxPeriodEndPreviewResponse } from '@/types/fx-period-end';
import { APIError } from '@/utils/api-client';

/** What the fund holds in each foreign currency, and what it is carried at. */
export const useForeignCashPositions = (fundId: string | undefined) => {
  return useApiQuery<ForeignCashPositionsResponse>(
    [...ledgerQueryKeys.all, 'foreign-cash-positions', fundId],
    () => currencyConversionService.listForeignCashPositions(fundId!),
    { enabled: !!fundId }
  );
};

/**
 * The working behind one period end, drafted or already posted. Keyed on the
 * transaction, not a date: the backend picks its source from that
 * transaction's status.
 */
export const useFxPeriodEndWorking = (
  fundId: string | undefined,
  fundTransactionId: string | undefined
) => {
  return useApiQuery<FxPeriodEndPreviewResponse>(
    [
      ...ledgerQueryKeys.all,
      'fx-period-end-working',
      fundId,
      fundTransactionId,
    ],
    () =>
      currencyConversionService.getPeriodEndWorking(
        fundId!,
        fundTransactionId!
      ),
    { enabled: !!fundId && !!fundTransactionId }
  );
};

/**
 * Discards a pending period end, freeing its reporting date. A draft blocks its
 * own date and every later one. The backend refuses anything already approved.
 */
export const useDiscardFxPeriodEnd = (fundId: string) => {
  return useApiMutation<void, APIError, string>(
    (fundTransactionId) =>
      currencyConversionService.discardPeriodEnd(fundId, fundTransactionId),
    {
      invalidateKeys: [
        queryKeys.fundTransactions.lists(fundId),
        ledgerQueryKeys.all,
      ],
      successMessage: 'FX period end discarded',
    }
  );
};

/**
 * Records a conversion as a DRAFT fund transaction. It reaches the ledger only
 * on approval, like every other cash event.
 */
export const useCreateCurrencyConversion = (fundId: string) => {
  return useApiMutation<
    CurrencyConversionRead,
    APIError,
    CurrencyConversionCreate
  >((payload) => currencyConversionService.create(fundId, payload), {
    invalidateKeys: [
      queryKeys.fundTransactions.lists(fundId),
      // Covers the foreign-cash-positions read, which is keyed beneath it.
      ledgerQueryKeys.all,
    ],
    successMessage: 'Currency conversion recorded as a draft',
  });
};

/** Records the conversion picked out of the incoming queue, still a draft. */
export const useRecordConversionFromBankLines = (fundId: string) => {
  return useApiMutation<
    CurrencyConversionCreateResponse,
    APIError,
    ConversionFromBankLinesCreate
  >(
    (payload) => currencyConversionService.recordFromBankLines(fundId, payload),
    {
      invalidateKeys: [
        queryKeys.fundTransactions.lists(fundId),
        // Covers the suggestions list and the foreign-cash positions.
        ledgerQueryKeys.all,
        // The legs and hops leave the queue.
        bankIncomingQueryKeys.all,
      ],
      successMessage: 'Currency conversion recorded from the bank lines',
    }
  );
};

/**
 * Derived on every read, so accepting one drops it out and a trade whose second
 * leg lands later appears as soon as it does.
 */
export const useConversionSuggestions = (fundId: string | undefined) => {
  return useApiQuery<ConversionProposal[]>(
    [...ledgerQueryKeys.all, 'currency-conversion-suggestions', fundId],
    () => currencyConversionService.listSuggestions(fundId!),
    { enabled: !!fundId }
  );
};

/** The draft still waits for approval, like one entered by hand. */
export const useAcceptConversionSuggestion = (fundId: string) => {
  return useApiMutation<CurrencyConversionRead, APIError, string>(
    (tradeReference) =>
      currencyConversionService.acceptSuggestion(fundId, tradeReference),
    {
      invalidateKeys: [
        queryKeys.fundTransactions.lists(fundId),
        // Covers the suggestions list and the foreign-cash positions, both
        // keyed beneath it - the accepted trade leaves one and opens the other.
        ledgerQueryKeys.all,
        // The legs and every funding hop leave the queue, and nothing under
        // this root shares a prefix with the two above.
        bankIncomingQueryKeys.all,
      ],
      successMessage: 'Currency conversion recorded from the bank statement',
    }
  );
};

/**
 * Fetched per proposal: an operator opens one row at a time, so building every
 * chain up front would be a query each for answers nobody asked for.
 */
export const useConversionSuggestionFundingChain = (
  fundId: string,
  tradeReference: string | undefined
) => {
  return useApiQuery<FundingChain | null>(
    [
      ...ledgerQueryKeys.all,
      'conversion-suggestion-funding-chain',
      fundId,
      tradeReference,
    ],
    () =>
      currencyConversionService.getSuggestionFundingChain(
        fundId,
        tradeReference!
      ),
    { enabled: !!tradeReference }
  );
};

/**
 * The legs and funding hops go back to the queue, and the trade is offered as a
 * suggestion again - the snapshot's reference was what said it was held.
 */
export const useDeleteCurrencyConversion = (fundId: string) => {
  return useApiMutation<void, APIError, string>(
    (fundTransactionId) =>
      currencyConversionService.deleteDraft(fundId, fundTransactionId),
    {
      invalidateKeys: [
        queryKeys.fundTransactions.lists(fundId),
        // Covers the suggestions list, keyed beneath it: the trade is back on
        // offer the moment its draft is gone.
        ledgerQueryKeys.all,
        // The legs and hops it held go back to the queue.
        bankIncomingQueryKeys.all,
      ],
      successMessage: 'Draft conversion deleted',
    }
  );
};

/**
 * Null when the conversion has no trade reference to follow, which is every one
 * entered by hand.
 */
export const useConversionFundingChain = (
  fundId: string | undefined,
  fundTransactionId: string | undefined
) => {
  return useApiQuery<FundingChain | null>(
    [
      ...ledgerQueryKeys.all,
      'currency-conversion-funding-chain',
      fundId,
      fundTransactionId,
    ],
    () =>
      currencyConversionService.getFundingChain(fundId!, fundTransactionId!),
    { enabled: !!fundId && !!fundTransactionId }
  );
};
